<template>
  <div class="raid-event-tabs">
    <div class="raid-event-tabs__header">
      <div
        ref="tabsContainer"
        class="raid-event-tabs__content">
        <div
          v-for="(day) in formattedDays"
          :key="day.key"
          ref="tabRefs"
          :data-key="day.key"
          class="raid-event-tabs__tab"
          :class="{'raid-event-tabs__tab--active': activeTabKey === day.key}"
          @click="setActiveTab(day.key)">
          <span
            class="raid-event-tabs__day"
            :class="{'raid-event-tabs__day--single': !day.dateLabel}">
            {{ day.dayLabel }}
          </span>

          <span
            v-if="day.dateLabel"
            class="raid-event-tabs__date">
            {{ day.dateLabel }}
          </span>
        </div>
      </div>

      <div
        class="raid-event-tabs__indicator"
        :style="indicatorStyle" />
    </div>

    <div class="raid-event-slots">
      <div class="raid-event-slots__wrapper">
        <div
          ref="timeSlotsList"
          class="raid-event-slots__list"
          @wheel="handleWheel"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove">
          <div
            v-for="slot in activeDaySlots"
            :key="slot.dateTime"
            class="raid-event-slots__item"
            :class="{'raid-event-slots__item--active': selectedSlot === slot}"
            @click="selectTimeSlot(slot)">
            {{ slot.time }}
          </div>
        </div>
      </div>

      <div class="raid-event-slots__controls">
        <button
          class="raid-event-slots__control raid-event-slots__control--prev"
          @click="scrollTimeSlots('prev')">
          <arrowhead-icon class="raid-event-slots__arrow" />
        </button>

        <button
          class="raid-event-slots__control raid-event-slots__control--next"
          @click="scrollTimeSlots('next')">
          <arrowhead-icon class="raid-event-slots__arrow" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import { mapState } from 'vuex';

export default {
  name: 'RaidEventTabs',

  components: {
    ArrowheadIcon,
  },

  props: {
    raidEvents: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      activeTabKey: null,
      selectedSlot: null,
      touchStartX: 0,
      indicatorWidth: 0,
      indicatorLeft: 0,
    };
  },

  computed: {
    ...mapState(['activeRegion']),

    formattedDays() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const formatDate = (date) => {
        const offset = date.getTimezoneOffset();
        const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
        return adjustedDate.toISOString().split('T')[0];
      };

      const todayKey = formatDate(today);
      const tomorrowKey = formatDate(tomorrow);

      const allDates = Object.keys(this.eventsByDay).sort();
      const result = [];

      if (this.eventsByDay[todayKey]) {
        result.push({
          key: todayKey,
          dayLabel: 'Today',
          dateLabel: '',
        });
      }

      if (this.eventsByDay[tomorrowKey]) {
        result.push({
          key: tomorrowKey,
          dayLabel: 'Tomorrow',
          dateLabel: '',
        });
      }

      const futureDates = allDates
        .filter((date) => date > tomorrowKey)
        .sort();

      futureDates.forEach((date) => {
        const dateObj = new Date(date);
        const dayLabel = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(dateObj);
        const dateLabel = new Intl.DateTimeFormat('en-US', {
          day: '2-digit',
          month: '2-digit',
        }).format(dateObj).replace('/', '.');

        result.push({
          key: date,
          dayLabel,
          dateLabel,
        });
      });

      return result.slice(0, 7);
    },

    eventsByDay() {
      const eventsByDay = {};

      this.raidEvents.forEach((event) => {
        const eventTimeString = event.time.endsWith('Z') ? event.time : `${event.time}Z`;
        const eventDate = new Date(eventTimeString);

        const fullDate = eventDate.toLocaleDateString('en-CA');

        const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: this.activeRegion.regionCode === 'US' };

        const time = eventDate.toLocaleTimeString('en-US', timeOptions);

        if (!eventsByDay[fullDate]) {
          eventsByDay[fullDate] = [];
        }

        eventsByDay[fullDate].push({
          time,
          slots: event.slots,
          raidId: event.raidId,
          dateTime: event.time,
        });
      });

      return eventsByDay;
    },

    activeDaySlots() {
      if (!this.activeTabKey || !this.eventsByDay) {
        return [];
      }

      const activeDay = this.activeTabKey;
      return this.eventsByDay[activeDay] || [];
    },

    indicatorStyle() {
      return {
        width: `${this.indicatorWidth}px`,
        transform: `translateX(${this.indicatorLeft}px)`,
      };
    },

    activeTabIndex() {
      return this.formattedDays.findIndex((day) => day.key === this.activeTabKey);
    },
  },

  watch: {
    raidEvents: {
      handler() {
        this.$nextTick(() => {
          this.selectedSlot = null;
          this.syncActiveTab();
        });
      },
      deep: true,
    },

    formattedDays: {
      handler() {
        this.$nextTick(() => {
          this.syncActiveTab();
          this.updateIndicator();
        });
      },
      deep: true,
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.formattedDays.length > 0) {
        this.activeTabKey = this.formattedDays[0].key;
      }

      this.updateIndicator();
      window.addEventListener('resize', this.updateIndicator);
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateIndicator);
  },

  methods: {
    syncActiveTab() {
      if (!this.formattedDays.length) {
        this.activeTabKey = null;
        return;
      }

      if (this.activeTabKey && this.formattedDays.some((day) => day.key === this.activeTabKey)) {
        return;
      }

      const today = this.formattedDays.find((day) => day.dayLabel === 'Today');
      if (today) {
        this.activeTabKey = today.key;
        return;
      }

      this.activeTabKey = this.formattedDays[0].key;
    },

    setActiveTab(key) {
      const selectedTab = this.formattedDays.find((day) => day.key === key);

      if (selectedTab) {
        this.activeTabKey = selectedTab.key;
        this.$nextTick(() => {
          this.updateIndicator();
        });
      }
    },

    updateIndicator() {
      const activeTab = this.formattedDays.find((day) => day.key === this.activeTabKey);
      if (!activeTab) {
        return;
      }

      const activeTabElement = this.$refs.tabRefs.find((tab) => tab.getAttribute('data-key') === activeTab.key);
      if (!activeTabElement) {
        return;
      }

      const { width, left } = activeTabElement.getBoundingClientRect();
      const containerLeft = this.$refs.tabsContainer.getBoundingClientRect().left;

      this.indicatorWidth = width;
      this.indicatorLeft = left - containerLeft;
    },

    selectTimeSlot(slot) {
      this.selectedSlot = slot;
      this.$emit('select-time-slot', slot);
    },

    scrollTimeSlots(direction) {
      const container = this.$refs.timeSlotsList;
      const scrollAmount = container.offsetWidth / 2;

      if (direction === 'prev') {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }
    },

    handleWheel(event) {
      event.preventDefault();
      this.$refs.timeSlotsList.scrollLeft += event.deltaY;
    },

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
    },

    handleTouchMove(event) {
      const touchEndX = event.touches[0].clientX;
      const diff = this.touchStartX - touchEndX;
      this.$refs.timeSlotsList.scrollLeft += diff;
      this.touchStartX = touchEndX;
    },
  },
};
</script>

<style lang="scss" scoped>
.raid-event-tabs__content {
  display: grid;
  grid-auto-flow: column;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: rgba($color--basic-blue, 0.25);
  }
}

.raid-event-tabs__header {
  display: grid;
  grid-auto-flow: column;
  position: relative;
  margin-bottom: 10px;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.raid-event-tabs__indicator {
  position: absolute;
  bottom: 0;
  height: 2px;
  background-color: $color--basic-blue;
  transition: all 0.3s ease;
  z-index: 1;
}

.raid-event-tabs__tab {
  padding: 8px 0;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  white-space: nowrap;
}

.raid-event-tabs__day {
  font-size: 15px;
  color: $color--basic-white;
  font-weight: 500;
  margin-right: 5px;
}

.raid-event-tabs__day--single {
  margin-right: 0;
}

.raid-event-tabs__date {
  font-size: 15px;
  color: $color--basic-superdark-gray;
}

.raid-event-slots {
  position: relative;
  margin-top: 18px;
}

.raid-event-slots__wrapper {
  overflow: hidden;
  padding: 0 30px;
}

.raid-event-slots__list {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.raid-event-slots__item {
  padding: 13px 16px;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  background-color: rgba($color--basic-blue, 0.25);
  border: 1px solid rgba($color--basic-blue, 0.25);
  border-radius: 8px;
  flex: 0 0 auto;
  margin-right: 10px;
  flex-shrink: 0;
  white-space: nowrap;

  &:last-child {
    margin-right: 0;
  }
  &:hover {
    background-color: rgba($color--basic-blue, 0.45);
    color: $color--basic-white;
    border: 1px solid $color--basic-blue;
  }
}

.raid-event-slots__item--active {
  background-color: rgba($color--basic-blue, 0.65);
  color: $color--basic-white;
  border: 1px solid $color--basic-blue;
}

.raid-event-slots__controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}

.raid-event-slots__control {
  background-color: transparent;
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

.raid-event-slots__control--prev {
  margin-left: -13px;
}

.raid-event-slots__control--prev .raid-event-slots__arrow {
  transform: rotate(90deg);
}

.raid-event-slots__control--next {
  margin-right: -13px;
}

.raid-event-slots__control--next .raid-event-slots__arrow {
  transform: rotate(-90deg);
}

.raid-event-slots__arrow {
  color: $color--basic-white;
}

@media screen and ($screen-less-than-768) {
  .raid-event-tabs {
    max-width: 100vw;
  }

  .raid-event-slots__wrapper {
    padding: 0 20px;
  }

  .raid-event-slots__item {
    min-width: auto;
    padding: 10px 12px;
  }
}

@media screen and ($screen-less-than-480) {
  .raid-event-tabs__tab {
    padding: 8px 10px;
  }
}
</style>
